import {CornerOutsets, StickMaterial, StickTermination} from "./StickMaterial";
import {MdCornerInfo, MdFrameJoint, MdFrameStick, cornerDefaultJoint} from "../../model/frame/MdFrameBlock";
import {MdFrameSpec} from "../../model/frame/MdFrameSpec";
import {FrmVar} from "../../drawfrm/FrmVar";
import {geo, Geo} from "./Joints";
import {findStickById} from "./BlockOps";
import {inches} from "../../const";

// alignment slop when matching stick ends and faces.
const WIGGLE = inches / 16;

// how far above a floor rail's top a jamb may stop and still turn the corner onto it.
const FLOOR_REACH = 2 * inches;

const CAP: StickTermination = {kind: "cap"};

// one end of a corner: the stick piece, which of its ends lands at the corner, and
// which side of the corner the piece lies on. `extend` is how far the end runs past
// its nominal end when it extends to own the corner.
type CornerEnd = {mat: StickMaterial; end: "A" | "B"; side: "top" | "bottom" | "left" | "right"; extend: FrmVar};

// a corner of the frame outline: a horizontal and a vertical stick piece that meet end
// to end, with nothing else filling the box where they meet. `floor` is a corner where
// a jamb meets a rail on the floor line. `through` names the piece that ran on across
// the box to the other's far face before detectCorners trimmed it back (a step's
// mullion piece over a void, or a jamb down to the floor); null when both stopped at
// the other's near face. `inner` is the corner inside a step, where the two-faced
// part of a mullion meets the step stick: it keeps the mullion running through.
export type FrameCorner = {
    key: string;
    label: string;
    h: CornerEnd;
    v: CornerEnd;
    floor: boolean;
    through: "h" | "v" | null;
    inner: boolean;
};

// a laid-out cell, for what lies beside a corner box: open is a void.
export type CornerCell = {open: boolean; rect: Rect};

type Rect = {x: number; y: number; width: number; height: number};
type Box = {xMin: number; xMax: number; yMin: number; yMax: number};

// find the corners of the laid-out outline. A horizontal piece's end must stop at a
// vertical piece's face, and the vertical piece's end at the horizontal piece's face,
// so the two leave a corner box (the vertical's thickness by the horizontal's) between
// them. A box another piece fills is a tee, not a corner (e.g. a rail butting into a
// mullion that runs on past it).
//
// One of the two may instead run on across the box and stop at the other's far face.
// That is a corner only when the box's two outer sides are both open (outside the
// frame or a void): an outer step corner, or a jamb down to the floor beside a void.
// With neither side open it is the inner corner of a step. With one side open it is a
// tee (a door jamb down to the floor beside the sill). At a corner the piece that ran
// through is trimmed back to the other's near face, so the terminations own the box.
export function detectCorners(sticks: StickMaterial[], floorY: number, cells: CornerCell[], frame: Rect | undefined): FrameCorner[] {
    const visible = sticks.filter(s => !s.destroyed && s.graphic.visible && s.length.get() > 0);
    const geos = new Map(visible.map(s => [s, geo(s)]));
    const out: FrameCorner[] = [];

    for(const h of visible) {
        if(h.orientation !== "horizontal") continue;
        const hg = geos.get(h)!;
        const floor = Math.abs(hg.crossMax - floorY) < WIGGLE;

        for(const v of visible) {
            if(v.orientation !== "vertical") continue;
            const vg = geos.get(v)!;

            const hEnd = endAt(hg, vg);
            if(!hEnd) continue;

            // the jamb's end sits on the rail's near face — or, at the floor, anywhere
            // close above the rail, as long as it doesn't already run down to the floor.
            const vEnd = endAt(vg, hg)
                ?? (floor && vg.alongMax < hg.crossMax - WIGGLE && vg.alongMax > hg.crossMin - FLOOR_REACH ? {end: "B" as const, through: false} : null);
            if(!vEnd || (hEnd.through && vEnd.through)) continue;

            // the corner box: across the jamb's thickness, and from the jamb's end to
            // the rail's far face.
            const box: Box = {
                xMin: vg.crossMin, xMax: vg.crossMax,
                yMin: vEnd.through || vEnd.end === "A" ? hg.crossMin : vg.alongMax,
                yMax: vEnd.through || vEnd.end === "B" ? hg.crossMax : vg.alongMin,
            };
            if(visible.some(o => o !== h && o !== v && overlaps(geos.get(o)!, box))) continue;

            const through = hEnd.through ? "h" : vEnd.through ? "v" : null;
            let inner = false;
            if(through) {
                const open = openSides(box, hEnd.end, vEnd.end, cells, frame);
                if(open === 1) continue;
                inner = open === 0;

                if(hEnd.through) {
                    if(hEnd.end === "A") h.graphic.position.set(vg.crossMax, h.positionY);
                    h.length.set(hEnd.end === "A" ? hg.alongMax - vg.crossMax : vg.crossMin - hg.alongMin, {debug: "corner-trim"});
                } else {
                    if(vEnd.end === "A") v.graphic.position.set(v.positionX, hg.crossMax);
                    v.length.set(vEnd.end === "A" ? vg.alongMax - hg.crossMax : hg.crossMin - vg.alongMin, {debug: "corner-trim"});
                }
            }

            const vy = vEnd.through ? (vEnd.end === "A" ? hg.crossMax : hg.crossMin) : (vEnd.end === "A" ? vg.alongMin : vg.alongMax);
            const vExtend = vEnd.end === "A" ? vy - hg.crossMin : hg.crossMax - vy;

            const hSide = vEnd.end === "A" ? "top" : "bottom";
            const vSide = hEnd.end === "A" ? "left" : "right";
            out.push({
                key: `${h.id}:${hEnd.end}|${v.id}:${vEnd.end}`,
                label: `${hSide === "top" ? "Top" : "Bottom"} ${vSide === "left" ? "Left" : "Right"}${inner ? " (step)" : ""}`,
                h: {mat: h, end: hEnd.end, side: hSide, extend: v.thickness},
                v: {mat: v, end: vEnd.end, side: vSide, extend: new FrmVar(`${v.id}:corner-extend`, vExtend)},
                floor,
                through,
                inner,
            });
        }
    }

    return out;
}

// which end of `g` lands at `other`: on its near face, or (through) at its far face
// with `g` running on across its thickness. Null when neither end does.
function endAt(g: Geo, other: Geo): {end: "A" | "B"; through: boolean} | null {
    if(Math.abs(g.alongMin - other.crossMax) < WIGGLE) return {end: "A", through: false};
    if(Math.abs(g.alongMax - other.crossMin) < WIGGLE) return {end: "B", through: false};
    if(Math.abs(g.alongMin - other.crossMin) < WIGGLE && g.alongMax > other.crossMax + WIGGLE) return {end: "A", through: true};
    if(Math.abs(g.alongMax - other.crossMax) < WIGGLE && g.alongMin < other.crossMin - WIGGLE) return {end: "B", through: true};
    return null;
}

// how many of the corner box's two outer sides (the side past the rail's end, and the
// side past the jamb's end) are open: outside the frame, or in a void.
function openSides(box: Box, hEnd: "A" | "B", vEnd: "A" | "B", cells: CornerCell[], frame: Rect | undefined): number {
    const w = box.xMax - box.xMin;
    const t = box.yMax - box.yMin;
    const past = hEnd === "A"
        ? {xMin: box.xMin - w, xMax: box.xMin, yMin: box.yMin, yMax: box.yMax}
        : {xMin: box.xMax, xMax: box.xMax + w, yMin: box.yMin, yMax: box.yMax};
    const beyond = vEnd === "A"
        ? {xMin: box.xMin, xMax: box.xMax, yMin: box.yMin - t, yMax: box.yMin}
        : {xMin: box.xMin, xMax: box.xMax, yMin: box.yMax, yMax: box.yMax + t};

    const isOpen = (probe: Box) => {
        if(frame && !boxesOverlap(rectBox(frame), probe)) return true;
        return cells.some(c => c.open && boxesOverlap(rectBox(c.rect), probe));
    };
    return (isOpen(past) ? 1 : 0) + (isOpen(beyond) ? 1 : 0);
}

function rectBox(r: Rect): Box {
    return {xMin: r.x, xMax: r.x + r.width, yMin: r.y, yMax: r.y + r.height};
}

// does a stick overlap the box by more than the alignment slop?
function overlaps(g: Geo, box: Box): boolean {
    const vertical = g.orientation === "vertical";
    return boxesOverlap({
        xMin: vertical ? g.crossMin : g.alongMin,
        xMax: vertical ? g.crossMax : g.alongMax,
        yMin: vertical ? g.alongMin : g.crossMin,
        yMax: vertical ? g.alongMax : g.crossMax,
    }, box);
}

function boxesOverlap(a: Box, b: Box): boolean {
    return Math.min(a.xMax, b.xMax) - Math.max(a.xMin, b.xMin) > WIGGLE
        && Math.min(a.yMax, b.yMax) - Math.max(a.yMin, b.yMin) > WIGGLE;
}

// cut every stick: a corner end per its joint, every other end square (a cap), which
// matches the structural rules — a tee stick caps where it meets the run, a child
// stick caps into its parent's rail. Returns the corners for the ribbon (see
// MdFrameSpec.cornerInfo).
//
// A corner joint is read from the stick's own model override (terminateA/B) when the
// end is the model stick's own end, else it uses the default: a floor corner extends
// the jamb to the floor; a step's inner corner runs the mullion through and caps the
// step stick on it; any other corner follows the frame-wide default.
// BlockOps.setCornerJoint keeps the two ends of a corner complementary (extend vs.
// cap), so reading each end on its own stays consistent.
export function resolveTerminations(spec: MdFrameSpec, sticks: StickMaterial[], corners: FrameCorner[], def: MdFrameJoint, floorY: number): MdCornerInfo[] {
    const terms = new Map<StickMaterial, {A: StickTermination; B: StickTermination}>();
    const info: MdCornerInfo[] = [];

    for(const c of corners) {
        const hStick = findStickById(spec, c.h.mat.stickId);
        const vStick = findStickById(spec, c.v.mat.stickId);

        const hDefault: MdFrameJoint = c.inner ? (c.through === "h" ? "extend" : "cap") : defaultJoint("horizontal", c.floor, def);
        const vDefault: MdFrameJoint = c.inner ? (c.through === "v" ? "extend" : "cap") : defaultJoint("vertical", c.floor, def);
        const hJoint = (c.h.mat.modelEnds[c.h.end] ? endJoint(hStick, c.h.end) : undefined) ?? hDefault;
        const vJoint = (c.v.mat.modelEnds[c.v.end] ? endJoint(vStick, c.v.end) : undefined) ?? vDefault;

        setEnd(terms, c.h.mat, c.h.end, terminationFor(hJoint, c.h, c.v));
        setEnd(terms, c.v.mat, c.v.end, terminationFor(vJoint, c.v, c.h));

        info.push({
            key: c.key,
            label: c.label,
            h: {id: c.h.mat.id, end: c.h.end, editable: c.h.mat.modelEnds[c.h.end]},
            v: {id: c.v.mat.id, end: c.v.end, editable: c.v.mat.modelEnds[c.v.end]},
            locked: c.floor || c.inner,
            allowMiter: true,
            defaults: {h: hDefault, v: vDefault},
        });
    }

    // a jamb whose bottom end has no corner and stops just above the floor (beside the
    // door's open sill, where no rail is drawn) extends down to the floor. One that
    // stands on bottom rails (one, or two that meet under it) caps on them.
    const cornered = new Set(corners.map(c => `${c.v.mat.id}:${c.v.end}`));
    const rails = sticks.filter(s => !s.destroyed && s.graphic.visible && s.orientation === "horizontal").map(geo);
    for(const s of sticks) {
        if(s.destroyed || !s.graphic.visible || s.orientation !== "vertical" || cornered.has(`${s.id}:B`)) continue;

        const g = geo(s);
        const gap = floorY - g.alongMax;
        if(gap <= WIGGLE || gap >= FLOOR_REACH + WIGGLE) continue;

        if(railsCover(rails, g.alongMax, g.crossMin, g.crossMax)) continue;

        setEnd(terms, s, "B", {kind: "extend", otherThickness: new FrmVar(`${s.id}:floor-extend`, gap)});
    }

    // an end with no corner that stops in a sloped stick's band (a mullion under a
    // sloped head, a rail beside a sloped jamb) caps on that band's inner face: each
    // corner runs to the face where it lies.
    const shown = sticks.filter(s => !s.destroyed && s.graphic.visible && s.length.get() > 0);
    const setEnds = new Set([
        ...corners.flatMap(c => [`${c.h.mat.id}:${c.h.end}`, `${c.v.mat.id}:${c.v.end}`]),
        ...Array.from(terms.keys()).filter(s => terms.get(s)!.B.kind === "extend").map(s => `${s.id}:B`),
    ]);
    for(const s of shown) {
        for(const end of ["A", "B"] as const) {
            if(setEnds.has(`${s.id}:${end}`)) continue;
            const corners = slopedCapOutsets(s, end, shown);
            if(corners) setEnd(terms, s, end, {kind: "cap", corners});
        }
    }

    for(const s of sticks) {
        if(s.destroyed) continue;
        const t = terms.get(s);
        s.setTerminations(t?.A ?? CAP, t?.B ?? CAP);
    }

    return info;
}

// the cap outsets for the `end` of `s` when it stops inside the band of a sloped
// perpendicular stick: each corner runs to the inner face of the band over it (under
// a peak the two corners lie under different sticks). Null when neither does.
function slopedCapOutsets(s: StickMaterial, end: "A" | "B", shown: StickMaterial[]): CornerOutsets | null {
    const g = geo(s);
    const at = end === "A" ? g.alongMin : g.alongMax;
    // the body of this stick lies past the end toward high coordinates at its A end.
    const bodyHigh = end === "A";

    // the outset of the corner at `cross`, to the inner face of a sloped band there.
    const cornerOutset = (cross: number): number | null => {
        for(const o of shown) {
            if(o === s || o.orientation === s.orientation || !o.hasSlope()) continue;
            const og = geo(o);
            if(cross < og.alongMin - WIGGLE || cross > og.alongMax + WIGGLE) continue;

            const f = o.facesAt(cross - og.alongMin);
            const base = o.orientation === "vertical" ? o.positionX : o.positionY;
            const faces = [base + f.left, base + f.right];
            const inner = bodyHigh ? Math.max(...faces) : Math.min(...faces);
            const outer = bodyHigh ? Math.min(...faces) : Math.max(...faces);

            // the end must lie in the band here (the trim puts it at the deepest
            // point across this stick, so the other corner may be past the band).
            const slack = Math.abs(inner - outer);
            if(at < outer - slack - WIGGLE || at > inner + slack + WIGGLE) continue;
            return end === "A" ? at - inner : inner - at;
        }
        return null;
    };

    const left = cornerOutset(g.crossMin);
    const right = cornerOutset(g.crossMax);
    if(left === null && right === null) return null;
    return {left: left ?? 0, right: right ?? 0};
}

// true when the horizontal `rails` whose top face lies at `y` cover the span
// `from`..`to` with no gap.
function railsCover(rails: Geo[], y: number, from: number, to: number): boolean {
    const under = rails
        .filter(r => Math.abs(r.crossMin - y) < WIGGLE && r.alongMax > from && r.alongMin < to)
        .sort((a, b) => a.alongMin - b.alongMin);

    let reached = from;
    for(const r of under) {
        if(r.alongMin > reached + WIGGLE) return false;
        reached = Math.max(reached, r.alongMax);
    }
    return reached > to - WIGGLE;
}

function setEnd(terms: Map<StickMaterial, {A: StickTermination; B: StickTermination}>, mat: StickMaterial, end: "A" | "B", term: StickTermination) {
    const cur = terms.get(mat) ?? {A: CAP, B: CAP};
    cur[end] = term;
    terms.set(mat, cur);
}

// the joint a corner end takes without a model override.
function defaultJoint(orientation: "horizontal" | "vertical", floor: boolean, def: MdFrameJoint): MdFrameJoint {
    return cornerDefaultJoint(floor ? "bottom" : "top", orientation, def);
}

function terminationFor(joint: MdFrameJoint, self: CornerEnd, other: CornerEnd): StickTermination {
    // meeting a sloped stick, each corner runs to that stick's face where the corner
    // lies (a sloped stick's own corners follow its band, so it needs nothing more).
    const corners = other.mat.hasSlope() && !self.mat.hasSlope() ? slopedCornerOutsets(joint, self, other) : undefined;
    switch(joint) {
        case "miter":
            return {kind: "miter", otherThickness: other.mat.thickness, direction: miterDirection(self.side), corners};
        case "extend":
            return {kind: "extend", otherThickness: self.extend, corners};
        case "cap":
        default:
            return {kind: "cap", corners};
    }
}

// the outsets of `self`'s two end corners where it meets the sloped stick `other`:
// a cap stops both at other's inner face, an extend runs both to its outer face, and
// a miter runs the outer corner to the outer face and the inner corner to the inner
// face, each taken at the corner's own position along `other`.
function slopedCornerOutsets(joint: MdFrameJoint, self: CornerEnd, other: CornerEnd): CornerOutsets {
    const g = geo(self.mat);
    const og = geo(other.mat);
    const nominal = self.end === "A" ? g.alongMin : g.alongMax;
    const base = other.mat.orientation === "vertical" ? other.mat.positionX : other.mat.positionY;
    // other lies on the low-coordinate side of the corner (top or left) when its outer
    // face is its Left (min cross) face.
    const outerIsLeft = other.side === "top" || other.side === "left";

    const faceAt = (cross: number, face: "outer" | "inner") => {
        const f = other.mat.facesAt(cross - og.alongMin);
        return base + ((face === "outer") === outerIsLeft ? f.left : f.right);
    };
    const outset = (target: number) => self.end === "A" ? nominal - target : target - nominal;

    // self's outer corner is on the outer side of the corner box: its Left corner when
    // self lies on the top or left of the corner.
    const outerCorner: "left" | "right" = self.side === "top" || self.side === "left" ? "left" : "right";
    const crossOf = (corner: "left" | "right") => corner === "left" ? g.crossMin : g.crossMax;
    const pick = (corner: "left" | "right") => {
        const face: "outer" | "inner" = joint === "extend" ? "outer" : joint === "miter" && corner === outerCorner ? "outer" : "inner";
        return outset(faceAt(crossOf(corner), face));
    };
    return {left: pick("left"), right: pick("right")};
}

// the miter direction keeps the *outer* end corner extending past the nominal end
// (StickMaterial.outset() outsets the corner that isn't the direction). A rail on the
// top side of its corner, or a jamb on the left side, has its outer face on its low
// side (→ "right"); the others on their high side (→ "left").
function miterDirection(side: CornerEnd["side"]): "left" | "right" {
    return side === "top" || side === "left" ? "right" : "left";
}

function endJoint(stick: MdFrameStick | undefined, end: "A" | "B"): MdFrameJoint | undefined {
    if(!stick) return undefined;
    return end === "A" ? stick.terminateA : stick.terminateB;
}
